/** WebVTT reader for the Courses page — enough of the format to build
 * a clickable transcript and a chapter list from the files the
 * user-guide videos ship with. The <track> element still gets the raw
 * file; this only feeds the panels beside the player, so it errs on the
 * side of reading what is there rather than rejecting a file the
 * browser would have played. */

export interface VttCue {
  start: number;
  end: number;
  text: string;
}

// "hh:mm:ss.mmm" or "mm:ss.mmm". A comma before the milliseconds is
// accepted too: files converted from SRT often keep it.
const TIMESTAMP = String.raw`(?:\d+:)?\d{1,2}:\d{2}[.,]\d{1,3}`;
// Cue settings ("line:0 position:20%") may follow the end time.
const TIMING_LINE = new RegExp(`^\\s*(${TIMESTAMP})\\s*-->\\s*(${TIMESTAMP})(?:\\s.*)?$`);
// A file made by concatenating several .vtt files repeats this line,
// sometimes with the next file's byte order mark still in front of it.
const HEADER_LINE = /^\uFEFF?WEBVTT(?:[ \t].*)?$/;
const SKIPPED_BLOCK = /^(?:NOTE|STYLE|REGION)(?:[ \t].*)?$/;

const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', nbsp: '\u00A0', lrm: '\u200E', rlm: '\u200F',
};

function toSeconds(stamp: string): number {
  const parts = stamp.replace(',', '.').split(':').map(Number);
  return parts.reduce((total, part) => total * 60 + part, 0);
}

function cleanText(lines: string[]): string {
  return lines
    .join(' ')
    // Voice, class, italic/bold/underline and karaoke timestamp tags.
    // Removed before entities are decoded so an escaped "&lt;b&gt;"
    // survives as literal text.
    .replace(/<[^>]*>/g, '')
    // One pass, so "&amp;lt;" decodes to "&lt;" and not to "<".
    .replace(/&(?:(amp|lt|gt|nbsp|lrm|rlm)|#(\d+)|#x([0-9a-f]+));/gi,
      (whole, name, dec, hex) => {
        if (name) return ENTITIES[name.toLowerCase()];
        const code = dec ? parseInt(dec, 10) : parseInt(hex, 16);
        return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole;
      })
    .replace(/\s+/g, ' ')
    .trim();
}

export function parseVtt(text: string): VttCue[] {
  const cues: VttCue[] = [];
  let current: { start: number; end: number; lines: string[] } | null = null;
  // Inside a header, NOTE, STYLE or REGION block: ignore until a blank line.
  let skipping = false;

  const flush = () => {
    if (current) {
      const cueText = cleanText(current.lines);
      // A cue holding only markup or direction marks has nothing to show.
      if (cueText.replace(/[\u200E\u200F]/g, '').trim()) {
        cues.push({ start: current.start, end: current.end, text: cueText });
      }
    }
    current = null;
  };

  for (const line of text.replace(/^\uFEFF/, '').split(/\r\n|\r|\n/)) {
    if (!line.trim()) {
      flush();
      skipping = false;
      continue;
    }
    if (HEADER_LINE.test(line)) {
      flush();
      skipping = true;
      continue;
    }
    const timing = TIMING_LINE.exec(line);
    if (timing) {
      // Also ends a header that ran straight into its first cue.
      flush();
      skipping = false;
      current = { start: toSeconds(timing[1]), end: toSeconds(timing[2]), lines: [] };
      continue;
    }
    if (current) {
      current.lines.push(line);
    } else if (!skipping && SKIPPED_BLOCK.test(line)) {
      skipping = true;
    }
    // Anything else is a cue identifier or header metadata.
  }
  flush();
  return cues;
}

/** "m:ss", or "h:mm:ss" from an hour up. */
export function formatTime(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}
